/**
 * eventService — Firestore persistence for the `events` collection (Calendar v2).
 *
 * Portal-native group classes / rehearsals / recitals. Mirrors timeSlotService
 * deliberately: a thin wrapper set where the caller builds the exact document
 * shape (including its own serverTimestamp fields) and these helpers perform the
 * raw Firestore op by id. The MMS importer never writes here — `events` is
 * portal-only, so coexistence with the MMS `lessons` collection is automatic.
 *
 * Occurrence semantics are lifted from `timeSlots` (isRecurring / dayOfWeek /
 * startDate / endDate for recurring; specificDate for single; an `exceptions`
 * map reserved for the fast-follow per-occurrence override). No new engine.
 *
 * Writes are admin/owner-only (enforced in firestore.rules + the Calendar UI
 * gate). Events are low-volume, so the read is an all-fetch with client-side
 * window filtering — no composite index required.
 */

import { db } from '../firebase';
import {
	collection,
	doc,
	query,
	getDocs,
	addDoc,
	updateDoc,
	deleteDoc,
} from 'firebase/firestore';

const COLLECTION = 'events';

// All events (low volume). The Calendar filters by the visible window client-
// side, so no `where`/`orderBy` composite index is needed.
export async function fetchAllEvents() {
	const snapshot = await getDocs(query(collection(db, COLLECTION)));
	return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addEvent(data) {
	return addDoc(collection(db, COLLECTION), data);
}

export async function updateEvent(id, data) {
	return updateDoc(doc(db, COLLECTION, id), data);
}

export async function deleteEvent(id) {
	return deleteDoc(doc(db, COLLECTION, id));
}
